import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useValidation } from '../../hooks/useValidation';
import { emailValidation, securePasswordValidation, passwordValidation, requiredValidation, cpfCnpjValidation } from '../../utils/validationUtils';
import { ErrorHandler } from '../../utils/errorHandler';
import LoginLayout from './components/LoginLayout';
import { LoginForm, RegisterForm } from './components';

const LoginContainer: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginData, loginLoading, loginError, register, registerLoading, registerError, emailExists, cpfCnpjExists, checkEmailExists, checkCpfCnpjExists } = useAuth();

  const [isRegistering, setIsRegistering] = React.useState(false);
  const [checkingEmail, setCheckingEmail] = React.useState(false);
  const [checkingCpfCnpj, setCheckingCpfCnpj] = React.useState(false);

  const loginValidationRules = {
    email: [emailValidation],
    password: [passwordValidation],
  };

  const registerValidationRules = {
    email: [emailValidation],
    password: [securePasswordValidation],
    cpfCnpj: [cpfCnpjValidation],
    razaoSocial: [requiredValidation('Razão Social')],
  };

  const { getFieldProps, validateAll, errors, values } = useValidation(
    isRegistering ? registerValidationRules : loginValidationRules
  );

  // Processa erros usando o ErrorHandler padronizado
  const processedLoginError = React.useMemo(() => {
    if (!loginError) return null;

    // Se já é uma string processada (vem do useRequest), retorna diretamente
    if (typeof loginError === 'string') {
      return loginError;
    }

    // Caso contrário, processa como objeto de erro
    return ErrorHandler.getGeneralError(loginError);
  }, [loginError]);

  const processedRegisterError = React.useMemo(() => {
    if (!registerError) return null;

    // Se já é uma string processada (vem do useRequest), retorna diretamente
    if (typeof registerError === 'string') {
      return registerError;
    }

    // Caso contrário, processa como objeto de erro
    return ErrorHandler.getGeneralError(registerError);
  }, [registerError]);

  // Função para verificar email quando campo perde o foco
  const handleEmailBlur = React.useCallback(async () => {
    const email = values.email?.trim();
    if (email && !errors.email) {
      setCheckingEmail(true);
      try {
        await checkEmailExists(email);
      } catch (error) {
        console.error('Erro ao verificar email:', ErrorHandler.processApiError(error));
      } finally {
        setCheckingEmail(false);
      }
    }
  }, [values.email, errors.email, checkEmailExists]);

  // Função para verificar CNPJ quando campo perde o foco
  const handleCpfCnpjBlur = React.useCallback(async () => {
    const cpfCnpj = values.cpfCnpj?.trim();
    if (cpfCnpj && !errors.cpfCnpj) {
      setCheckingCpfCnpj(true);
      try {
        await checkCpfCnpjExists(cpfCnpj);
      } catch (error) {
        console.error('Erro ao verificar CPF/CNPJ:', ErrorHandler.processApiError(error));
      } finally {
        setCheckingCpfCnpj(false);
      }
    }
  }, [values.cpfCnpj, errors.cpfCnpj, checkCpfCnpjExists]);

  React.useEffect(() => {
    if (loginData && loginData.authenticated) {
      navigate('/dashboard');
    }
  }, [loginData, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateAll()) {
      return;
    }

    if (isRegistering) {
      // Verificar se email e CPF/CNPJ já existem antes de registrar
      const emailTaken = await checkEmailExists(values.email);
      const cpfCnpjTaken = await checkCpfCnpjExists(values.cpfCnpj || '');

      if (emailTaken || cpfCnpjTaken) {
        return; // Não prosseguir se já existir
      }

      // Registrar usuário
      register({
        email: values.email,
        password: values.password,
        tipo: 1, // Pessoa física por padrão
        cpfCnpj: values.cpfCnpj || '',
        razaoSocial: values.razaoSocial || '',
      });
    } else {
      // Fazer login
      login(values.email, values.password);
    }
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const formProps = {
    isRegistering,
    getFieldProps,
    errors,
    loginLoading,
    registerLoading,
    loginError: processedLoginError,
    registerError: processedRegisterError,
    emailExists,
    cpfCnpjExists,
    onSubmit: handleSubmit,
    onToggleMode: toggleMode,
    onForgotPassword: handleForgotPassword,
    onEmailBlur: isRegistering ? handleEmailBlur : undefined,
    onCpfCnpjBlur: handleCpfCnpjBlur,
    checkingEmail,
    checkingCpfCnpj,
  };

  return (
    <LoginLayout>
      {isRegistering ? (
        <RegisterForm {...formProps} />
      ) : (
        <LoginForm {...formProps} />
      )}
    </LoginLayout>
  );
};

export default LoginContainer;